import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/services/api';
import type { User, Role } from '@/types';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => !!user.value);
  const isSuperuser = computed(() => {
    if (!user.value) return false;
    if (user.value.is_superuser) return true;
    // Only grant superuser for the explicit system_admin role code
    const roles = user.value.roles || [];
    if (Array.isArray(roles)) {
      return roles.some((r: unknown) => {
        const code = typeof r === 'string' ? r : (r as Role)?.code;
        return code === 'system_admin';
      });
    }
    return false;
  });
  const displayName = computed(() => user.value?.display_name ?? (user.value as unknown as Record<string, string>)?.name ?? user.value?.email ?? '');
  const permissions = computed(() => {
    if (!user.value) return [];
    // Check top-level permissions (from /auth/me SSO response)
    const topLevel = (user.value as unknown as Record<string, unknown>).permissions;
    if (Array.isArray(topLevel) && topLevel.length > 0) {
      return topLevel as string[];
    }
    // Fall back to role-based permissions
    if (!user.value.roles) return [];
    return user.value.roles.flatMap((role: unknown) => {
      if (typeof role === 'string') return [];
      return (role as Role)?.permissions || [];
    });
  });

  function hasPermission(permission: string): boolean {
    if (isSuperuser.value) return true;
    if (permissions.value.includes('*')) return true;
    return permissions.value.includes(permission);
  }

  function hasAnyPermission(perms: string[]): boolean {
    if (isSuperuser.value) return true;
    if (permissions.value.includes('*')) return true;
    return perms.some((p) => permissions.value.includes(p));
  }

  async function loadUser() {
    loading.value = true;
    error.value = null;
    try {
      const data = await api.get<User>('/auth/me');
      // Normalize: ensure is_superuser is set based on roles (API may omit it)
      const roles = (data as unknown as Record<string, unknown>).roles;
      if (!data.is_superuser && Array.isArray(roles)) {
        (data as unknown as Record<string, unknown>).is_superuser = roles.some(
          (r: unknown) => r === 'system_admin'
        );
      }
      user.value = data;
      // Persist for next page load so sidebar renders immediately
      localStorage.setItem('moya_user', JSON.stringify(data));
    } catch (e) {
      error.value = 'Failed to load user';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function initiateLogin(): void {
    sessionStorage.setItem('moya_auth_return_url', window.location.href);
    const ssoUrl = import.meta.env.VITE_SSO_URL || 'https://login.moyasolutions.com';
    const callbackUrl = `${window.location.origin}/callback`;
    window.location.href = `${ssoUrl}/login?redirect_uri=${encodeURIComponent(callbackUrl)}`;
  }

  async function loginWithToken(token: string): Promise<void> {
    api.setToken(token);
    await loadUser();
  }

  function logout() {
    // clearSsoSession() (via clearToken) removes the localStorage keys, the
    // legacy bootstrap keys, and the domain-wide moya_token cookie. Clearing
    // localStorage alone would leave the cookie behind, and the index.html
    // bootstrap would silently restore the session on the next page load.
    api.clearToken();
    user.value = null;
    const ssoUrl = import.meta.env.VITE_SSO_URL || 'https://login.moyasolutions.com';
    const callbackUrl = `${window.location.origin}/callback`;
    window.location.href = `${ssoUrl}/login?redirect_uri=${encodeURIComponent(callbackUrl)}`;
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    isSuperuser,
    displayName,
    permissions,
    hasPermission,
    hasAnyPermission,
    loadUser,
    initiateLogin,
    loginWithToken,
    logout,
  };
});
